import { useTranslation } from 'react-i18next';
import type { Maybe, RemoteData } from '@passionware/monads';
import { MapPinX } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import type { GeoInfo } from 'v5/api/geolocation/geolocation.api';
import type { LocationValue } from 'v5/core/entity-values/values.types';
import { FormField } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { SuggestInput } from 'v5/patterns/forms/fields/SuggestInput';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponent } from './fieldComponent.types';

export type LocationFieldConfig = {
  /** Feature-provided search hook (the engine imports no services). */
  useLocations: (query: Maybe<string>) => RemoteData<GeoInfo[]>;
};

/**
 * One location: a name typed, or a place picked from the search (with its
 * coordinates, if the search gives any). At rest, its name.
 */
export function createLocationField(
  config: LocationFieldConfig
): FieldComponent<'location'> {
  return function LocationField({ spec, path, hideLabel }) {
    const { t } = useTranslation('patterns');
    const ctx = useFieldContext();
    const value: LocationValue | null = useWatch({ name: path }) ?? null;

    if (ctx.mode === 'display') {
      if (value === null || value.name.trim() === '') {
        return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
      }
      return (
        <FieldControlBase spec={spec} hideLabel={hideLabel}>
          <Input
            readOnly
            variant={ctx.variant}
            size={ctx.size}
            value={value.name}
          />
        </FieldControlBase>
      );
    }

    return (
      <FormField
        name={path}
        render={({ field }) => (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            <SuggestInput<GeoInfo>
              inputRef={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              value={field.value?.name ?? ''}
              onChange={name => field.onChange({ name, coordinates: null })}
              useSuggestions={config.useLocations}
              toOption={geo => ({
                id: geo.id,
                label: geo.name,
                detail: geo.description ?? null
              })}
              onPick={geo =>
                field.onChange({
                  name: geo.name,
                  coordinates: geo.coordinates ?? null
                })
              }
              emptyContent={
                <span className="flex items-center gap-2">
                  <MapPinX className="size-5" /> No locations found
                </span>
              }
              variant={ctx.variant}
              size={ctx.size}
              placeholder={t('SchemaForm.location.placeholder')}
              aria-label={spec.label}
            />
          </FieldControlBase>
        )}
      />
    );
  };
}
